import { useHandleError } from "@/hooks/useHandleError";
import { useCountNumberOfReferralUsage } from "@roman/shared/hooks/useCountNumberOfReferralUsage";
import { useGetOrCreateReferral } from "@roman/shared/hooks/useGetOrCreateReferral";
import type React from "react";
import { type HTMLAttributes, useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { Ticket } from "./Ticket";
import { ArrowButton } from "./arrowButton";

export const Invitation = () => {
  const [arrowEntryRequirement, setArrowEntryRequirement] = useState<boolean>(false);
  const handleGetOrCreateReferralError = useHandleError("招待コードの取得に失敗しました");
  const { data: referralCode, isLoading: isLoadingGetOrCreateReferral } =
    useGetOrCreateReferral(handleGetOrCreateReferralError);
  const handleCountNumberOfReferralUsageError = useHandleError("招待コードの使用回数の取得に失敗しました");
  const {
    data: referralUsageCount,
    trigger: countNumberOfReferralUsage,
    isLoading: isLoadingCountNumberOfReferralUsage,
  } = useCountNumberOfReferralUsage(handleCountNumberOfReferralUsageError);

  useEffect(() => {
    if (referralCode?.code) {
      countNumberOfReferralUsage({ referralCode: referralCode.code });
    }
  }, [referralCode?.code, countNumberOfReferralUsage]);

  if (!referralUsageCount || !referralCode) {
    return <Loading />;
  }

  const handleShareInvitationCode = () => {
    const shareText = `#ポイ活 #スポーツ予想  ならサンバ！
登録無料で誰でも気軽に遊べる🎁✨
招待コード【${referralCode.code}】
友達を招待するだけで最大400ポイントが無料で貰える！👫
今すぐポイントをGETして、賞金を獲得しよう！🔥
#サンバ`;

    // Open X in a new tab with pre-filled text
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent("https://thumba.jp")}`,
      "_blank",
    );
  };

  const handleArrowForEarningEntryPoints = () => {
    setArrowEntryRequirement((prev) => !prev);
  };

  const showEntryPointRequirement = (): React.ReactElement => {
    return (
      <div className="w-full flex flex-col items-start p-5 gap-2.5 bg-light-blue">
        <div className="flex flex-col items-start">
          <p className="text-xs font-bold text-dark-gray">・招待コード登録 & 電話番号の認証</p>
          <p className="text-xs font-bold text-dark-gray">
            招待された人が新規会員登録後に「①招待コード登録」「②電話番号の認証」で
            <span className="text-orange">300pt</span>獲得！ 招待された人も<span className="text-orange">300pt</span>
            獲得！
          </p>
        </div>
        <div className="flex flex-col items-start">
          <p className="text-xs font-medium text-gray">
            ※ 招待コードの入力は会員登録時してから24時間以内のみ適用されます。
          </p>
          <p className="text-xs font-medium text-gray">※ 招待できる人数には限りがあります。</p>
        </div>
        <div
          onClick={() => {
            window.open("https://half-pressure-b6c.notion.site/1800-1a6941dfbe4080448f10fba22ab5cba4?pvs=4", "_blank");
          }}
          className="flex items-center justify-end w-full gap-[7px] hover:cursor-pointer"
        >
          <p className="text-xs font-bold text-primary">招待コードとエントリーポイントについて</p>
          <img src="/assets/arrow.svg" alt="arrow button" className="rotate-90" />
        </div>
      </div>
    );
  };

  if (isLoadingGetOrCreateReferral || isLoadingCountNumberOfReferralUsage) {
    return <Loading />;
  }

  if (!referralUsageCount || !referralCode) {
    return <Loading />;
  }
  return (
    <div className="flex flex-col gap-[26px] items-start py-[39px] border-b border-gray-200 border-solid">
      <p className="text-lg font-semibold -tracking-[.01em]">招待コード</p>
      <div className="flex flex-col items-center gap-[26px] w-full">
        <div className="flex flex-col items-start gap-3 w-full">
          <div className="w-full flex items-center justify-center flex-col gap-2">
            <Ticket
              code={referralCode.code}
              freePointsEarned={Number(referralUsageCount.freePointsEarned)}
              numberOfUses={referralUsageCount.numberOfUses}
            />
          </div>
          <div className="text-xs font-bold -tracking-[.01em] text-disabled">
            友達がサンバをプレイして条件を達成すると<span className="text-orange">エントリーポイント</span>
            をプレゼント！
          </div>
          <div className="flex flex-col gap-[7px] w-full">
            <div className="flex item-center justify-end">
              <div
                className="flex gap-[7px] hover:cursor-pointer items-center"
                onClick={handleArrowForEarningEntryPoints}
              >
                <p className="text-xs font-bold text-primary">エントリーポイントの獲得条件</p>
                <ArrowButton className={`${arrowEntryRequirement && "rotate-0"}`} />
              </div>
            </div>
            {arrowEntryRequirement && showEntryPointRequirement()}
          </div>
        </div>

        <Button
          className="w-2/3 rounded-full border-primary font-bold text-primary hover:text-white hover:bg-primary/90"
          variant="outline"
          onClick={handleShareInvitationCode}
        >
          招待コードをシェア
        </Button>
      </div>
    </div>
  );
};

const Loading = (props: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className="flex flex-col rounded-lg text-base w-full bg-white p-1 gap-2" {...props}>
      <div className="mt-2 flex items-center flex-row justify-start">
        <Skeleton className="w-[89px] h-[28px]" />
      </div>
      <Skeleton className="h-[110px] w-full rounded-xl" />

      <div className="flex items-center justify-start">
        <Skeleton className="w-3/4 h-[16px]" />
      </div>
      <div className="flex items-center justify-end">
        <Skeleton className="w-1/3 h-[16px]" />
      </div>
      <div className="flex items-center justify-center">
        <Skeleton className="w-1/2 h-[27px]" />
      </div>
    </div>
  );
};
